import type { TScreenRotation } from '../models/screen-rotation'

export interface FrameRotationContainerBBox { width: number, height: number }
export interface FrameRotationLayoutCanvas { width: number, height: number }

function gcd(a: number, b: number): number {
  return a === 0 ? b : gcd(b % a, a)
}

function normalizeRotation(rotation: TScreenRotation | null | undefined): TScreenRotation {
  if (rotation === '90' || rotation === '180' || rotation === '270') {
    return rotation
  }
  return '0'
}

/**
 * Inline CSS for a playlist frame canvas centered in `bbox`, with optional display rotation.
 * Shared by {@link apps/player/src/components/VFrame.vue} and RSS directory playback.
 */
export function frameRotationContainerStyle(
  bbox: FrameRotationContainerBBox | null,
  layout: FrameRotationLayoutCanvas,
  rotation: TScreenRotation | null | undefined
): string {
  if (bbox === null) {
    return ''
  }
  const _rotation = normalizeRotation(rotation)
  let transform = ''
  switch (_rotation) {
    case '90':
      transform = 'transform: rotate(90deg);'
      break
    case '180':
      transform = 'transform: rotate(180deg);'
      break
    case '270':
      transform = 'transform: rotate(270deg);'
      break
  }

  const { width, height } = layout
  const d = gcd(width, height)
  const w = width / d
  const h = height / d

  const screenOrientationVertical = _rotation === '90' || _rotation === '270'
  const aspectRatio = w / h
  // Contain-scale layout canvas into viewport. Explicit px height is required so absolute
  // section children (height: 100%) resolve correctly on WebViews that ignore aspect-ratio.
  const scale = Math.min(bbox.width / width, bbox.height / height)
  const scaledWidth = width * scale
  const scaledHeight = height * scale
  const kh = bbox.height / height
  const style = [
    'position: relative',
    screenOrientationVertical
      ? `height: 100vw; max-height: ${(height / aspectRatio) * kh}px;`
      : `width: ${scaledWidth}px; height: ${scaledHeight}px;`,
    `aspect-ratio: ${w}/${h}`,
    transform
  ]
  return style.join(';')
}
